/** Duplicate. The server composes the copy (`… (copy)`, `DRAFT`, shared media objects); the editor
 *  is opened on the new row rather than the list refetched. */
import type { Ref } from 'vue'
import type { AssignmentTemplateListRow } from './interfaces'

import { useAssignmentTemplatesStore } from '../assignment-templates/store'
import { useAssignmentTemplateDuplicateApi } from '../../api/assignment-templates/duplicate.hook'
import { getAssignmentEditPath } from './assignment-template-options'

interface ModalDuplicateAssignmentTemplateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTemplate: ComputedRef<AssignmentTemplateListRow | null>
  refDetail: ComputedRef<string>
  closeModal: () => void
  confirmDuplicate: () => Promise<void>
}

export const useModalDuplicateAssignmentTemplate = (): ModalDuplicateAssignmentTemplateReturn => {
  const router = useRouter()
  const store = useAssignmentTemplatesStore()

  const refIsLoading = ref(false)

  const refIsOpen = computed<boolean>(() => store.isModalDuplicate)
  const refTemplate = computed<AssignmentTemplateListRow | null>(() => store.getTemplateToDuplicate)

  const refDetail = computed<string>(() =>
    `Create a draft copy of "${refTemplate.value?.title ?? ''}" and open it for editing. `
    + 'The original stays untouched.')

  const closeModal = (): void => {
    store.setTemplateToDuplicate(null)
  }

  const confirmDuplicate = async (): Promise<void> => {
    const template = refTemplate.value
    if (!template || refIsLoading.value) return

    await useAssignmentTemplateDuplicateApi({
      templateId: template.id,
      onStart: () => { refIsLoading.value = true },
      onSuccess: (created) => {
        closeModal()
        void router.push(getAssignmentEditPath(created.id))
      },
      onFinish: () => { refIsLoading.value = false },
    })
  }

  return {
    refIsOpen,
    refIsLoading,
    refTemplate,
    refDetail,
    closeModal,
    confirmDuplicate,
  }
}
